import type { DateFilterValue } from '@/components/Analysis/state';

export const TEMPLATE_DATE_FILTERS_QUERY_KEY = 'templateDateFilters';

type UrlDateFilterPayload =
  | { type: 'single'; date: string }
  | { type: 'range'; startDate: string; endDate: string };

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/** Encoded query value for `templateDateFilters`, or undefined when there is nothing to persist. */
export function serializeAppliedDateFiltersForUrl(
  applied: Record<string, DateFilterValue>
): string | undefined {
  const out: Record<string, UrlDateFilterPayload> = {};
  for (const [key, filter] of Object.entries(applied)) {
    if (!key) continue;
    if (filter.type === 'single' && isNonEmptyString(filter.date)) {
      out[key] = { type: 'single', date: filter.date };
    } else if (
      filter.type === 'range' &&
      isNonEmptyString(filter.startDate) &&
      isNonEmptyString(filter.endDate)
    ) {
      out[key] = {
        type: 'range',
        startDate: filter.startDate,
        endDate: filter.endDate,
      };
    }
  }
  if (Object.keys(out).length === 0) return undefined;
  return encodeURIComponent(JSON.stringify(out));
}

export function parseTemplateDateFiltersFromQueryParam(
  raw: string | (string | null)[] | null | undefined
): Record<string, DateFilterValue> | null {
  if (raw === undefined || raw === null || Array.isArray(raw)) return null;
  try {
    const decoded = decodeURIComponent(String(raw));
    const parsed: unknown = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const result: Record<string, DateFilterValue> = {};
    for (const [fieldKey, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!fieldKey || !value || typeof value !== 'object' || Array.isArray(value)) continue;
      const v = value as Record<string, unknown>;
      if (v.type === 'single' && isNonEmptyString(v.date)) {
        result[fieldKey] = { type: 'single', date: v.date };
      } else if (
        v.type === 'range' &&
        isNonEmptyString(v.startDate) &&
        isNonEmptyString(v.endDate)
      ) {
        result[fieldKey] = {
          type: 'range',
          startDate: v.startDate,
          endDate: v.endDate,
        };
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}
